import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import myobFirebaseService from "services/myobFirebaseService";
import { useAuthContext } from "context/auth-context";
import toast from "react-hot-toast";

const CallbackPage = () => {
    const [status, setStatus] = useState("processing");
    const [error, setError] = useState(null);
    const [tokenInfo, setTokenInfo] = useState(null);
    const navigate = useNavigate();
    const { currentUserData } = useAuthContext();

    useEffect(() => {
        const handleOAuthCallback = async () => {
            console.log("🔄 OAuth Callback started");
            console.log("Current user data:", currentUserData);
            
            try {
                // Check if user is authenticated
                if (!currentUserData?.id) {
                    console.log("❌ User not authenticated");
                    throw new Error("User not authenticated. Please login first.");
                }

                console.log("✅ User authenticated:", currentUserData.id);

                // Set current user for Firebase service
                myobFirebaseService.setCurrentUser(currentUserData.id);
                console.log("✅ Current user set in Firebase service");

                // Grab query parameters from the URL
                const params = new URLSearchParams(window.location.search);
                const code = params.get("code");
                const state = params.get("state");
                const error = params.get("error");
                const errorDescription = params.get("error_description");
                const businessId = params.get("businessId");
                const businessName = params.get("businessName");

                console.log("🔧 OAuth Callback Parameters:");
                console.log("Code:", code ? code.substring(0, 30) + "..." : "Not provided");
                console.log("State:", state);
                console.log("Business ID:", businessId);
                console.log("Business Name:", businessName);
                console.log("Error param:", error);
                console.log("Error description:", errorDescription);

                // Check for OAuth errors
                if (error) {
                    console.log("❌ OAuth error found:", error);
                    throw new Error(`OAuth Error: ${errorDescription || error}`);
                }

                // Check if we have the authorization code
                if (!code) {
                    console.log("❌ No authorization code received");
                    throw new Error("No authorization code received");
                }

                console.log("✅ Authorization code found, starting token exchange...");
                setStatus("exchanging");

                // Exchange code for tokens using Firebase service
                console.log("🔄 Calling myobFirebaseService.exchangeCodeForTokens...");
                const tokens = await myobFirebaseService.exchangeCodeForTokens(code, state, businessId, businessName);
                console.log("✅ Token exchange successful:", tokens);
                
                setStatus("success");
                const tokenInfo = myobFirebaseService.getTokensInfo();
                setTokenInfo(tokenInfo);
                toast.success("Successfully connected to MYOB! Tokens saved to database.");
                
                console.log("✅ MYOB tokens stored in Firebase successfully:", tokenInfo);
                
                // Redirect to dashboard after 3 seconds
                console.log("🔄 Setting redirect timer...");
                setTimeout(() => {
                    console.log("🔄 Redirecting to dashboard...");
                    navigate("/dashboard");
                }, 3000);

            } catch (error) {
                console.error("❌ OAuth callback error:", error);
                console.error("Error details:", {
                    message: error.message,
                    stack: error.stack,
                    name: error.name
                });
                setError(error.message);
                setStatus("error");
                toast.error(`MYOB connection failed: ${error.message}`);
            }
        };

        // Only run the callback if we have user data
        if (currentUserData?.id) {
            handleOAuthCallback();
        }
    }, [navigate, currentUserData]);

    const renderContent = () => {
        // Show loading state if user is not authenticated yet
        if (!currentUserData?.id) {
            // Store the callback URL so we can redirect back after login
            const callbackUrl = window.location.href;
            localStorage.setItem('myob_callback_url', callbackUrl);
            console.log("🔗 Stored callback URL for redirect after login:", callbackUrl);
            
            return (
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Required</h2>
                    <p className="text-gray-600 mb-4">You need to be logged in to complete the MYOB connection.</p>
                    <div className="space-y-2">
                        <button 
                            onClick={() => navigate("/sign-in")}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors mr-2"
                        >
                            Go to Sign In
                        </button>
                        <button 
                            onClick={() => navigate("/dashboard")}
                            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                    <div className="mt-4 p-4 bg-gray-100 rounded text-sm text-left">
                        <p><strong>Debug Info:</strong></p>
                        <p>currentUserData: {currentUserData ? 'Present' : 'null'}</p>
                        <p>currentUserData.id: {currentUserData?.id || 'null'}</p>
                        <p>URL: {window.location.href}</p>
                        <p>Callback URL stored: {callbackUrl}</p>
                    </div>
                </div>
            );
        }

        switch (status) {
            case "processing":
                return (
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Processing OAuth Callback</h2>
                        <p className="text-gray-600">Please wait while we complete the authentication...</p>
                    </div>
                );

            case "exchanging":
                return (
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Exchanging Authorization Code</h2>
                        <p className="text-gray-600">Getting your access tokens from MYOB...</p>
                    </div>
                );

            case "validating":
                return (
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Validating User</h2>
                        <p className="text-gray-600">Getting your user information from MYOB...</p>
                    </div>
                );

            case "success":
                return (
                    <div className="text-center">
                        <div className="text-green-600 text-6xl mb-4">✅</div>
                        <h2 className="text-2xl font-bold text-green-800 mb-4">MYOB Connected Successfully!</h2>
                        
                        {tokenInfo && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-left max-w-md mx-auto">
                                <h3 className="font-semibold text-green-800 mb-2">Connection Details:</h3>
                                <p><strong>Business:</strong> {tokenInfo.business_name}</p>
                                <p><strong>Business ID:</strong> {tokenInfo.business_id}</p>
                                {tokenInfo.user_id && (
                                    <>
                                        <p><strong>User ID:</strong> {tokenInfo.user_id}</p>
                                        <p><strong>Username:</strong> {tokenInfo.username}</p>
                                    </>
                                )}
                                <p><strong>Scope:</strong> {tokenInfo.scope}</p>
                                <p><strong>Expires:</strong> {tokenInfo.expires_at}</p>
                            </div>
                        )}
                        
                        <p className="text-gray-600 mb-4">
                            You will be redirected to the dashboard in a few seconds...
                        </p>
                        
                        <button 
                            onClick={() => navigate("/dashboard")}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                );

            case "error":
                return (
                    <div className="text-center">
                        <div className="text-red-600 text-6xl mb-4">❌</div>
                        <h2 className="text-2xl font-bold text-red-800 mb-4">Connection Failed</h2>
                        
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left max-w-md mx-auto">
                            <h3 className="font-semibold text-red-800 mb-2">Error Details:</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                        
                        <div className="space-y-2">
                            <button 
                                onClick={() => window.location.reload()}
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors mr-2"
                            >
                                Try Again
                            </button>
                            <button 
                                onClick={() => navigate("/dashboard")}
                                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                );

            default:
                return <p>Loading...</p>;
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
                {renderContent()}
            </div>
        </div>
    );
}

export default CallbackPage;